/**
 * CR-6348: Order Details section of the reworked SO Lines filter panel.
 * Text/search/dropdown/checkbox fields plus the re-introduced legacy status
 * filters (ODD Status, OTD Status to LSPD) and Critical-lines-only.
 *
 * CR-6348 rework: Per client feedback, OTIF Overridden, PPV and Buyer ID filters
 * are now visible to all users including suppliers (per client diagram).
 *
 * CR-7400: Buyer ID is a multi-select of the codes in scope, matched exactly,
 * instead of a free text "contains" box.
 */
import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import {
  POLinesFilters,
  STATUS_OPTIONS,
  ODD_STATUS_OPTIONS,
  OTD_LSPD_STATUS_OPTIONS,
} from '../../types';
import { FilterSection, FIELD_CLS, LABEL_CLS } from '../fieldStyles';
import { BuyerIdMultiSelect } from '../BuyerIdMultiSelect';

interface OrderDetailsSectionProps {
  filters: POLinesFilters;
  onFilterChange: (key: string, value: string) => void;
  // CR-6347: the customer view renders Part Number / Description in its own
  // Classification section, so it hides them here. Defaults preserve the
  // supplier view exactly.
  showPartAndDescription?: boolean;
  // CR-6347: extra order-detail fields injected by the caller (customer view
  // adds ERP Order Type + On Hold, which are feature-flag gated upstream).
  extraFields?: ReactNode;
  /** CR-6347: Optional icon for the section header */
  icon?: LucideIcon;
  /**
   * CR-7400: the ERP buyer codes present on the PO lines in scope
   * (filter-options `buyer_ids`). Empty list = the control has nothing to
   * offer and is disabled, which is honest: no line carries a code.
   */
  buyerIdOptions?: string[];
}

export function OrderDetailsSection({
  filters,
  onFilterChange,
  showPartAndDescription = true,
  extraFields,
  icon,
  buyerIdOptions = [],
}: OrderDetailsSectionProps) {
  return (
    <FilterSection title="Order Details" icon={icon}>
      {/* PO Number */}
      <div>
        <label className={LABEL_CLS}>PO Number</label>
        <input
          type="text"
          placeholder="620596..."
          value={filters.poNumber}
          onChange={(e) => onFilterChange('poNumber', e.target.value)}
          className={FIELD_CLS}
        />
      </div>

      {/* Line Number */}
      <div>
        <label className={LABEL_CLS}>Line #</label>
        <input
          type="text"
          placeholder="1, 2, 3..."
          value={filters.lineNumber}
          onChange={(e) => onFilterChange('lineNumber', e.target.value)}
          className={FIELD_CLS}
        />
      </div>

      {/* Batch / Serial No */}
      <div>
        <label className={LABEL_CLS}>Batch/Serial No</label>
        <input
          type="text"
          placeholder="Batch or serial..."
          value={filters.batchSerialNo}
          onChange={(e) => onFilterChange('batchSerialNo', e.target.value)}
          className={FIELD_CLS}
        />
      </div>

      {/* Buyer ID — CR-6348 rework: visible to all users per client diagram.
          CR-7400: a multi-select of the codes in scope, matched exactly. */}
      <div>
        <label className={LABEL_CLS}>Buyer ID</label>
        <BuyerIdMultiSelect
          options={buyerIdOptions}
          value={filters.buyerId}
          onChange={(value) => onFilterChange('buyerId', value)}
        />
      </div>

      {/* Part Number + Description — hidden in the customer view, which groups
          them under its own Classification section (CR-6347). */}
      {showPartAndDescription && (
        <>
          <div>
            <label className={LABEL_CLS}>Part Number</label>
            <input
              type="text"
              placeholder="Part number..."
              value={filters.partNumber}
              onChange={(e) => onFilterChange('partNumber', e.target.value)}
              className={FIELD_CLS}
            />
          </div>

          <div>
            <label className={LABEL_CLS}>Part Description</label>
            <input
              type="text"
              placeholder="Part description..."
              value={filters.partDescription}
              onChange={(e) => onFilterChange('partDescription', e.target.value)}
              className={FIELD_CLS}
            />
          </div>

          {/* Order Description — the line's own text. Its column is still on
              this grid (Aaron, 19 Sep), so it keeps its own box. */}
          <div>
            <label className={LABEL_CLS}>Order Description</label>
            <input
              type="text"
              placeholder="Order description..."
              value={filters.description}
              onChange={(e) => onFilterChange('description', e.target.value)}
              className={FIELD_CLS}
            />
          </div>
        </>
      )}

      {/* Status */}
      <div>
        <label className={LABEL_CLS}>Status</label>
        <select
          value={filters.status}
          onChange={(e) => onFilterChange('status', e.target.value)}
          className={FIELD_CLS}
        >
          <option value="">All Statuses</option>
          {STATUS_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>

      {/* Caller-injected fields (CR-6347 customer view: ERP Order Type, On Hold) */}
      {extraFields}

      {/* ODD Status (CR-6348) */}
      <div>
        <label className={LABEL_CLS}>ODD Status</label>
        <select
          value={filters.oddStatus}
          onChange={(e) => onFilterChange('oddStatus', e.target.value)}
          className={FIELD_CLS}
        >
          <option value="">All</option>
          {ODD_STATUS_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>

      {/* OTD Status to LSPD (CR-6348) */}
      <div>
        <label className={LABEL_CLS}>OTD Status to LSPD</label>
        <select
          value={filters.otdStatus}
          onChange={(e) => onFilterChange('otdStatus', e.target.value)}
          className={FIELD_CLS}
        >
          <option value="">All</option>
          {OTD_LSPD_STATUS_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>

      {/* Next Action */}
      <div>
        <label className={LABEL_CLS}>Next Action</label>
        <select
          value={filters.nextAction}
          onChange={(e) => onFilterChange('nextAction', e.target.value)}
          className={FIELD_CLS}
        >
          <option value="">All</option>
          <option value="customer">Waiting Customer</option>
          <option value="supplier">Waiting Supplier</option>
        </select>
      </div>

      {/* OTIF Status — CR-7416 (Aaron, 19 Sep): the measured VERDICT, the same
          three states the drill-downs badge. It sits immediately before OTIF
          Review on purpose: they both start with "OTIF" and they answer
          different questions — this one what the delivery DID, that one what a
          buyer DECIDED about it — so they have to be adjacent and read
          distinctly rather than turn up in separate corners of the panel.
          Distinct from "ODD Status" above, which is the six-valued adherence
          vocabulary and keeps the `otif_status` wire name. */}
      <div>
        <label className={LABEL_CLS}>OTIF Status</label>
        <select
          value={filters.otifVerdict}
          onChange={(e) => onFilterChange('otifVerdict', e.target.value)}
          className={FIELD_CLS}
        >
          <option value="">All</option>
          <option value="pending">Pending</option>
          <option value="passed">Passed</option>
          <option value="failed">Failed</option>
        </select>
      </div>

      {/* OTIF Review — CR-7418 (Aaron, 19 Sep): *"there is no way to filter
          on ack, overidden, not decided"*.

          This REPLACES the OTIF Overridden yes/no control that stood here
          (CR-6348). The two would have said almost the same thing side by side,
          which is the contradiction this platform keeps being asked to stop —
          and Decision is the finer answer: Overridden is one of its three
          values, and what used to be "Not Overridden" splits into the two
          states a reviewer actually cares about, Not Decided and Acknowledged.
          The one combination lost is "either of those two as a single choice".
          Server-side through the shared predicate (otif_review.py), so it is
          honest on every page of the set and not just the one on screen.

          Named OTIF REVIEW, matching the column, the badge and the pill
          (Aaron, 19 Sep: *"the filter name is mismatched, make it OTIF review
          as well so everything is named consistently"*). The WIRE is still
          `otif_decision` — the daily digest's deep links already in people's
          inboxes carry that parameter, and renaming it would break a link
          somebody clicks tomorrow for no gain anybody can see.

          On BOTH panels: a supplier filters by it too, read only (CR-6348,
          Aaron 5 Oct 2026). */}
      <div>
        <label className={LABEL_CLS}>OTIF Review</label>
        <select
          value={filters.otifDecision}
          onChange={(e) => onFilterChange('otifDecision', e.target.value)}
          className={FIELD_CLS}
        >
          <option value="">All</option>
          <option value="not_decided">Not Decided</option>
          <option value="overridden">Overridden</option>
          <option value="acknowledged">Acknowledged</option>
        </select>
      </div>

      {/* PPV — CR-6348 rework: now visible to all users per client feedback */}
      <div>
        <label className={LABEL_CLS}>PPV</label>
        <select
          value={filters.ppv}
          onChange={(e) => onFilterChange('ppv', e.target.value)}
          className={FIELD_CLS}
        >
          <option value="">All</option>
          <option value="positive">Positive (Cost Overrun)</option>
          <option value="negative">Negative (Savings)</option>
          <option value="zero">Zero (No Variance)</option>
        </select>
      </div>

      {/* Critical lines only (CR-6348) */}
      <div className="flex items-center pt-7">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={filters.criticalOnly === 'true'}
            onChange={(e) => onFilterChange('criticalOnly', e.target.checked ? 'true' : '')}
            className="w-4 h-4 rounded border-border text-primary focus:ring-primary focus:ring-2"
          />
          <span className="text-sm font-medium">Critical lines only</span>
        </label>
      </div>
    </FilterSection>
  );
}
